import React from 'react';
import { CpuChipIcon, ArrowsRightLeftIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const SEGMENTS = [
  {
    key: 'ai_only',
    label: '100% IA',
    description: 'Resolvidas sem nenhuma intervenção humana',
    color: '#4f46e5',
    badge: 'bg-indigo-50 text-indigo-700',
  },
  {
    key: 'hybrid',
    label: 'IA + humano',
    description: 'A IA atendeu e passou para um agente',
    color: '#38bdf8',
    badge: 'bg-sky-50 text-sky-700',
  },
  {
    key: 'human_only',
    label: '100% humano',
    description: 'Nenhuma mensagem da IA',
    color: '#f59e0b',
    badge: 'bg-amber-50 text-amber-700',
  },
];

const toNumber = (value) => Number(value) || 0;
const percent = (value, total) => (total ? (value / total) * 100 : 0);
const formatPercent = (value, total, digits = 1) =>
  total ? `${percent(value, total).toFixed(digits)}%` : '—';
const formatNumber = (value) => toNumber(value).toLocaleString('pt-BR');

/**
 * Volume de atendimento por autoria: IA, IA + humano e humano.
 *
 * A base é o total de conversas *respondidas* — conversas que nunca receberam
 * resposta ficam fora do percentual e são mostradas à parte. Sem isso o número
 * da IA seria diluído pelas conversas que ninguém atendeu (em algumas contas,
 * a maioria absoluta).
 */
const AiVsHuman = ({ data }) => {
  if (!data) return null;

  const answered = toNumber(data.answered);
  const unanswered = toNumber(data.unanswered);

  const segments = SEGMENTS.map((segment) => ({
    ...segment,
    value: toNumber(data[segment.key]),
    resolved: toNumber(data[`${segment.key}_resolved`]),
  }));

  const aiOnly = segments[0];
  const hybrid = segments[1];
  const humanOnly = segments[2];

  // Handoff: das conversas que a IA atendeu, quantas precisaram de humano
  const aiTouched = aiOnly.value + hybrid.value;
  const handoffRate = formatPercent(hybrid.value, aiTouched);

  if (answered === 0) {
    return (
      <div className="mb-6 rounded-lg bg-white p-6 shadow">
        <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold">
          <CpuChipIcon className="h-5 w-5 text-indigo-600" />
          Atendimento: IA × Humano
        </h3>
        <p className="text-gray-500">
          Nenhuma conversa respondida no período — {formatNumber(unanswered)} sem resposta.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 overflow-hidden rounded-lg bg-white shadow">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-6 py-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <CpuChipIcon className="h-5 w-5 text-indigo-600" />
          Atendimento: IA × Humano
        </h3>
        <span className="text-xs text-gray-500">
          base: {formatNumber(answered)} conversas respondidas
          {unanswered > 0 && ` · ${formatNumber(unanswered)} sem resposta ficam fora`}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[minmax(0,15rem)_1fr]">
        {/* Destaque: autonomia da IA */}
        <div className="flex flex-col justify-center rounded-lg bg-gradient-to-br from-indigo-50 to-white p-5 ring-1 ring-indigo-100">
          <span className="text-5xl font-bold leading-none text-indigo-600">
            {formatPercent(aiOnly.value, answered, 1)}
          </span>
          <span className="mt-2 text-sm font-medium text-gray-900">resolvido só pela IA</span>
          <span className="mt-1 text-xs text-gray-500">
            {formatNumber(aiOnly.value)} de {formatNumber(answered)} conversas respondidas
          </span>
        </div>

        <div className="flex flex-col justify-center">
          {/* Barra empilhada */}
          <div className="flex h-8 w-full overflow-hidden rounded-md bg-gray-100">
            {segments.map((segment) =>
              segment.value > 0 ? (
                <div
                  key={segment.key}
                  style={{
                    width: `${percent(segment.value, answered)}%`,
                    backgroundColor: segment.color,
                  }}
                  className="group relative transition-all"
                  title={`${segment.label}: ${formatNumber(segment.value)} (${formatPercent(segment.value, answered)})`}
                >
                  {percent(segment.value, answered) >= 12 && (
                    <span className="flex h-full items-center justify-center text-xs font-semibold text-white">
                      {formatPercent(segment.value, answered, 1)}
                    </span>
                  )}
                </div>
              ) : null
            )}
          </div>

          {/* Legenda */}
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {segments.map((segment) => (
              <div key={segment.key} className="flex items-start gap-2">
                <span
                  className="mt-1 h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatNumber(segment.value)}{' '}
                    <span className="font-normal text-gray-500">
                      ({formatPercent(segment.value, answered)})
                    </span>
                  </p>
                  <p className="text-xs font-medium text-gray-700">{segment.label}</p>
                  <p className="text-xs text-gray-500">{segment.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Indicadores derivados */}
      <div className="grid grid-cols-1 divide-y divide-gray-100 border-t border-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <div className="flex items-center gap-3 px-6 py-4">
          <div className="rounded-full bg-indigo-50 p-2">
            <CpuChipIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">{formatNumber(aiOnly.value)}</p>
            <p className="text-xs text-gray-500">conversas fechadas sem humano</p>
          </div>
        </div>

        <div className="flex items-center gap-3 px-6 py-4">
          <div className="rounded-full bg-sky-50 p-2">
            <ArrowsRightLeftIcon className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">{handoffRate}</p>
            <p className="text-xs text-gray-500">
              das conversas que a IA atendeu passaram para um agente
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 px-6 py-4">
          <div className="rounded-full bg-green-50 p-2">
            <UserGroupIcon className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900">
              {formatPercent(aiOnly.resolved, aiOnly.value, 0)}
              <span className="mx-1 text-sm font-normal text-gray-400">×</span>
              {formatPercent(humanOnly.resolved, humanOnly.value, 0)}
            </p>
            <p className="text-xs text-gray-500">
              taxa de resolução: só IA × só humano
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiVsHuman;
